import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, AlertCircle, RefreshCw, Eye, EyeOff, Zap } from "lucide-react";
import DealForgeLogo from "../components/DealForgeLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteActivationPage({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [phase, setPhase] = useState("validating"); // validating | form | success | payment | error
  const [errorMsg, setErrorMsg] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setPhase("error");
      setErrorMsg("No invite token found in this link. Check your email for the correct link.");
      return;
    }

    axios.get(`${BACKEND_URL}/api/auth/invite/validate?token=${token}`)
      .then(res => {
        setInviteInfo(res.data);
        setForm(f => ({ ...f, name: res.data.name || "" }));
        setPhase("form");
      })
      .catch(err => {
        setPhase("error");
        setErrorMsg(err.response?.data?.detail || "This invite link is invalid or has expired.");
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter your name"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords don't match"); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/auth/register-via-invite`,
        { token, name: form.name.trim(), password: form.password },
        { withCredentials: true }
      );

      // Call the parent onLogin handler so the app state updates
      if (onLogin && res.data.user) {
        onLogin(res.data.user);
      }

      // Show payment step instead of going to dashboard
      setPhase("payment");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account. Try again or contact support.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartSubscription = async () => {
    setCheckoutLoading(true);
    try {
      // First, select portals (default to investor)
      await axios.post(
        `${BACKEND_URL}/api/subscription/select-portals`,
        { primary_portal: "investor", additional_portals: [] },
        { withCredentials: true }
      );
      
      // Create checkout session
      const res = await axios.post(
        `${BACKEND_URL}/api/subscription/create-checkout`,
        {},
        { withCredentials: true }
      );
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Failed to create checkout. Please try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment setup failed. You can retry from Settings.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-6"
      style={{ backgroundImage: "radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.04) 0%, transparent 70%)" }}>

      {/* Logo */}
      <div className="mb-10">
        <DealForgeLogo size="md" showTM />
      </div>

      <div className="w-full max-w-md">
        {/* Validating */}
        {phase === "validating" && (
          <div className="text-center py-16">
            <RefreshCw className="w-8 h-8 animate-spin text-[#D4AF37]/60 mx-auto mb-4" />
            <p className="text-white/40 text-sm">Validating your invite...</p>
          </div>
        )}

        {/* Error */}
        {phase === "error" && (
          <div className="bg-[#0A0A0A] border border-red-500/20 p-8 text-center">
            <AlertCircle className="w-10 h-10 text-red-400/60 mx-auto mb-4" />
            <h2 className="text-white font-bold text-lg mb-2">Invite Link Issue</h2>
            <p className="text-white/50 text-sm mb-6">{errorMsg}</p>
            <p className="text-white/30 text-xs">
              Questions? Email{" "}
              <a href="mailto:support@dealforge.pro" className="text-[#D4AF37] hover:underline">
                support@dealforge.pro
              </a>
            </p>
          </div>
        )}

        {/* Success */}
        {phase === "success" && (
          <div className="bg-[#0A0A0A] border border-emerald-500/30 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            </div>
            <h2 className="text-white font-bold text-xl mb-2">Account Activated</h2>
            <p className="text-white/50 text-sm mb-1">Welcome to DealForge, {form.name.split(" ")[0]}.</p>
            <p className="text-[#D4AF37]/60 text-xs">Redirecting to your dashboard...</p>
          </div>
        )}

        {/* Payment Step */}
        {phase === "payment" && (
          <div className="bg-[#0A0A0A] border border-white/[0.08]">
            {/* Header */}
            <div className="px-8 py-6 border-b border-white/[0.06]">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-sm">
                  <CheckCircle2 className="w-3 h-3" /> Account Created
                </span>
              </div>
              <h1 className="text-xl font-bold text-white mb-1">One last step, {form.name.split(" ")[0]}</h1>
              <p className="text-white/40 text-sm">
                Activate your Founder membership — $99/mo, locked forever.
              </p>
            </div>

            {/* Pricing Card */}
            <div className="px-8 py-6">
              <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30 p-6 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-white font-bold text-lg">Founder Membership</h3>
                    <p className="text-[#D4AF37] text-xs font-semibold">Founder rate — locked forever</p>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-white/40 uppercase tracking-wider mb-0.5">Then</div>
                    <span className="text-3xl font-bold text-[#D4AF37]">$99</span>
                    <span className="text-white/40 text-sm">/mo</span>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-[#D4AF37] font-medium">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                    <span>$99/mo — price locked in permanently as a Founder</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                    <span>AI-powered deal analysis & guidance</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                    <span>Execution Exchange marketplace access</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                    <span>All partner portals included</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                    <span>Permanent Founder badge on profile</span>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-[#D4AF37]/20">
                  <p className="text-[#D4AF37]/60 text-xs text-center">
                    $99/month. Cancel anytime. Price locked forever for founders.
                  </p>
                </div>
              </div>

              <Button
                onClick={handleStartSubscription}
                disabled={checkoutLoading}
                className="w-full h-12 bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold text-base"
                data-testid="start-subscription-btn"
              >
                {checkoutLoading 
                  ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Redirecting to checkout...</>
                  : "Start Free Trial"
                }
              </Button>
            </div>
          </div>
        )}

        {/* Registration Form */}
        {phase === "form" && inviteInfo && (
          <div className="bg-[#0A0A0A] border border-white/[0.08]">
            {/* Header */}
            <div className="px-8 py-6 border-b border-white/[0.06]">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 rounded-sm">
                  <Zap className="w-3 h-3" /> Founder Invite
                </span>
              </div>
              <h1 className="text-xl font-bold text-white mb-1">Activate your account</h1>
              <p className="text-white/40 text-sm">
                Invited as <span className="text-white/70">{inviteInfo.email}</span>
              </p>
            </div>

            {/* Benefits strip */}
            <div className="px-8 py-4 bg-[#D4AF37]/[0.03] border-b border-[#D4AF37]/10">
              <div className="flex items-center gap-4 text-[11px] text-white/40">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#D4AF37]" /> Founder pricing locked</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#D4AF37]" /> Founder badge</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#D4AF37]" /> Full platform access</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
              <div>
                <Label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">Full Name</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="James DeHart"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/20 h-11"
                  data-testid="invite-name-input"
                  autoFocus
                />
              </div>

              <div>
                <Label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">Email</Label>
                <Input
                  value={inviteInfo.email}
                  disabled
                  className="bg-white/[0.03] border-white/[0.06] text-white/50 h-11 cursor-not-allowed"
                />
              </div>

              <div>
                <Label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">Create Password</Label>
                <div className="relative">
                  <Input
                    type={showPw ? "text" : "password"}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Min. 8 characters"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/20 h-11 pr-10"
                    data-testid="invite-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {form.password && (
                  <div className="mt-1.5 flex gap-1">
                    {[4, 6, 8, 10, 12].map(len => (
                      <div
                        key={len}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          form.password.length >= len ? "bg-[#D4AF37]" : "bg-white/10"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">Confirm Password</Label>
                <Input
                  type="password"
                  value={form.confirm}
                  onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                  placeholder="Repeat password"
                  className={`bg-white/5 border-white/10 text-white placeholder:text-white/20 h-11 ${
                    form.confirm && form.confirm !== form.password ? "border-red-500/50" : ""
                  }`}
                  data-testid="invite-confirm-input"
                />
                {form.confirm && form.confirm !== form.password && (
                  <p className="text-red-400/70 text-xs mt-1">Passwords don't match</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={submitting || !form.name || !form.password || form.password !== form.confirm}
                className="w-full h-12 bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold text-base mt-2"
                data-testid="activate-account-btn"
              >
                {submitting
                  ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Creating account...</>
                  : "Activate Account"
                }
              </Button>

              <p className="text-center text-white/20 text-xs pt-1">
                Already have an account?{" "}
                <Link to="/login" className="text-[#D4AF37]/60 hover:text-[#D4AF37]">Sign in</Link>
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
